import {
  Autocomplete,
  debounce,
  FormControl,
  Grid,
  TextField,
} from "@mui/material";
import FormGridRow from "../../../../common/components/functional/formGridRow/FormGridRow";
import { useTranslation } from "react-i18next";
import { Controller, FieldValues, useFormContext } from "react-hook-form";
import { SpinnerButton } from "../../../../common/components/functional/spinnerButton/SpinnerButton";
import useIsLoading from "../../../../hooks/useIsLoading";
import { CustomButton } from "../../../../common/components/functional/customButton/CustomButton";
import { useEffect, useState } from "react";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { fetchEezyCompanies } from "../../../invoices/invoiceSlice";
import { isFulfilled } from "@reduxjs/toolkit";
import { useAppDispatch } from "../../../../app/hooks";
import { EezyCompany, EezyCostCenter } from "../../../invoices/invoiceAPI";
import { customerOrgAndCompAutocomplete } from "../../../customer/customerAPI";
import { NO_DRAG_SELECTOR } from "../../../../app/constants";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";
import { NumericFormat } from "react-number-format";
import {
  CreateEmployerHealthInsuranceReduction,
  UpdateEmployerHealthInsuranceReduction,
} from "../types";
import { format, startOfMonth } from "date-fns";
import { yearMonthFormat } from "../../../../common/helpers/formatDateTime";
import {
  addEmployerHealthInsuranceReduction,
  updateEmployerHealthInsuranceReduction,
} from "../employerHealthInsuranceReductionsSlice";
import { CustomerOrg } from "../../../entries/types";
import { CustomerOrgCompany } from "../../../../common/types/apiTypes";

type Props = {
  onClose: () => void;
};

export const EmployerHealthInsuranceReductionForm = ({ onClose }: Props) => {
  const dispatch = useAppDispatch();
  const {
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
    watch,
    setValue,
  } = useFormContext();
  const { t } = useTranslation();

  const isLoading = useIsLoading(["employerHealthInsuranceReductions"]);
  const canSave = isValid && isDirty;

  const id: string = watch("id");
  const eezyCompany: EezyCompany = watch("employer");

  const [eezyCompanyOptions, setEezyCompanyOptions] = useState<EezyCompany[]>(
    [],
  );
  const [costCenterOptions, setCostCenterOptions] = useState<EezyCostCenter[]>(
    [],
  );
  const [customerOrgOptions, setCustomerOrgOptions] = useState<CustomerOrg[]>(
    [],
  );
  const [customerSearchString, setCustomerSearchString] = useState<string>("");

  const createNew = (data: FieldValues) => {
    const payload: CreateEmployerHealthInsuranceReduction = {
      employerId: data.employer.eezyCompanyId,
      employerName: data.employer.name,
      employerCostCenterId: data.employerCostCenter.eezyCostCenterId,
      employerCostCenterName: data.employerCostCenter.name,
      ...(data.customerOrganization?.id && {
        customerOrganizationId: data.customerOrganization.id,
        customerOrganizationName: data.customerOrganization.name,
      }),
      yearMonth: format(data.yearMonth!!, yearMonthFormat),
      amount: data.amount,
      ...(data.identifier && { identifier: data.identifier }),
    };
    dispatch(addEmployerHealthInsuranceReduction(payload)).then((action) => {
      if (isFulfilled(action)) {
        onClose();
      }
    });
  };

  const updateExisting = (data: FieldValues) => {
    const payload: UpdateEmployerHealthInsuranceReduction = {
      id,
      version: data.version,
      employerId: data.employer.eezyCompanyId,
      employerName: data.employer.name,
      employerCostCenterId: data.employerCostCenter.eezyCostCenterId,
      employerCostCenterName: data.employerCostCenter.name,
      ...(data.customerOrganization && {
        customerOrganizationId: data.customerOrganization.id,
        customerOrganizationName: data.customerOrganization.name,
      }),
      yearMonth: format(data.yearMonth!!, yearMonthFormat),
      amount: data.amount,
      ...(data.identifier && { identifier: data.identifier }),
    };
    dispatch(updateEmployerHealthInsuranceReduction(payload)).then((action) => {
      if (isFulfilled(action)) {
        onClose();
      }
    });
  };

  const onSubmit = async (data: FieldValues) => {
    if (id) {
      updateExisting(data);
    } else {
      createNew(data);
    }
  };

  const dispatchCustomerOrgSearch = (searchString: string) => {
    try {
      customerOrgAndCompAutocomplete({ name: searchString }).then((res) => {
        const customerCompanies: CustomerOrgCompany[] = res;
        const customerOrgs: CustomerOrg[] = [];
        customerCompanies.forEach((company) => {
          company.customerOrganizations.forEach((org) => {
            customerOrgs.push(org);
          });
        });
        setCustomerOrgOptions(customerOrgs);
      });
    } catch (e: any) {
      console.error("Failed to fetch CustomerOrganizations: ", e);
    }
  };

  const handleCustomerSearchStringChange = debounce(
    (value: string) => setCustomerSearchString(value),
    500,
  );

  useEffect(() => {
    if (customerSearchString !== "") {
      dispatchCustomerOrgSearch(customerSearchString);
    }
  }, [customerSearchString]);

  useEffect(() => {
    dispatch(fetchEezyCompanies({})).then(
      (action) => isFulfilled(action) && setEezyCompanyOptions(action.payload),
    );
  }, [dispatch]);

  useEffect(() => {
    if (eezyCompany && eezyCompany.costCenters) {
      setCostCenterOptions(eezyCompany.costCenters);
    } else {
      setCostCenterOptions([]);
    }
  }, [eezyCompany]);

  // Get employer cost centers for edit view
  useEffect(() => {
    if (id) {
      const employer = eezyCompanyOptions.find(
        (company) => company.eezyCompanyId === eezyCompany?.eezyCompanyId,
      );
      if (employer) {
        setCostCenterOptions(employer.costCenters);
      }
    }
  }, [id, eezyCompanyOptions, eezyCompany?.eezyCompanyId]);

  return (
    <Grid
      container
      rowSpacing={1}
      columnSpacing={2}
      className={NO_DRAG_SELECTOR}
    >
      <FormGridRow label={t("employer")} labelSize={4} fieldSize={8}>
        <Controller
          control={control}
          name="employer"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <FormControl fullWidth>
              <Autocomplete
                value={value}
                size="small"
                options={eezyCompanyOptions}
                getOptionLabel={(option) => option?.name || ""}
                onChange={(_: any, newValue: any) => {
                  setValue("employerCostCenter", null);
                  onChange(newValue);
                }}
                renderInput={(params: any) => (
                  <TextField name="employer" {...params} />
                )}
              />
            </FormControl>
          )}
        />
      </FormGridRow>
      <FormGridRow
        label={t("talentProfile:costCenter")}
        labelSize={4}
        fieldSize={8}
      >
        <Controller
          control={control}
          name="employerCostCenter"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <FormControl fullWidth>
              <Autocomplete
                value={value}
                size="small"
                options={costCenterOptions}
                getOptionLabel={(option) => option?.name || ""}
                onChange={(_: any, newValue: any) => onChange(newValue)}
                renderInput={(params: any) => (
                  <TextField name="employerCostCenter" {...params} />
                )}
              />
            </FormControl>
          )}
        />
      </FormGridRow>
      <FormGridRow
        label={t("customerOrganization")}
        labelSize={4}
        fieldSize={8}
      >
        <Controller
          control={control}
          name="customerOrganization"
          render={({ field: { value, onChange } }) => (
            <FormControl fullWidth>
              <Autocomplete
                value={value}
                size="small"
                options={customerOrgOptions}
                noOptionsText={t("common:writeToSearch")}
                getOptionLabel={(option) => option?.name || ""}
                onChange={(_: any, newValue: any) => onChange(newValue)}
                onInputChange={(_, newValue: string) =>
                  handleCustomerSearchStringChange(newValue)
                }
                renderInput={(params: any) => (
                  <TextField name="customerOrganization" {...params} />
                )}
              />
            </FormControl>
          )}
        />
      </FormGridRow>
      <FormGridRow label={t("payslips:monthYear")} labelSize={4} fieldSize={8}>
        <Controller
          control={control}
          name="yearMonth"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => {
            const today = new Date();
            const firstOfMonth = startOfMonth(today);
            return (
              <FormControl fullWidth>
                <DesktopDatePicker
                  name="yearMonth"
                  value={value}
                  onChange={onChange}
                  views={["month", "year"]}
                  minDate={firstOfMonth}
                  format="MM/yy"
                  slotProps={{
                    textField: {
                      error: !!errors["yearMonth"],
                      size: "small",
                      fullWidth: true,
                      onKeyDown: (e) => e.preventDefault(),
                    },
                  }}
                />
              </FormControl>
            );
          }}
        />
      </FormGridRow>
      <FormGridRow
        label={t("talentProfile:amount")}
        labelSize={4}
        fieldSize={8}
      >
        <Controller
          control={control}
          name="amount"
          rules={{
            required: true,
            validate: (value) => value.slice(-1) !== ".",
          }}
          render={({ field: { value, onChange } }) => (
            <FormControl fullWidth>
              <NumericFormat
                value={value}
                decimalSeparator=","
                valueIsNumericString
                customInput={TextField}
                decimalScale={2}
                size="small"
                onValueChange={(values) => {
                  onChange(values.value);
                }}
              />
            </FormControl>
          )}
        />
      </FormGridRow>
      <FormGridRow label={t("payslips:identifier")} labelSize={4} fieldSize={8}>
        <Controller
          control={control}
          name="identifier"
          rules={{ maxLength: 128 }}
          render={({ field: { value, onChange } }) => (
            <FormControl fullWidth>
              <CommonTextField
                name="identifier"
                value={value}
                onChange={onChange}
                error={!!errors["identifier"]}
                size="small"
                fullWidth
              />
            </FormControl>
          )}
        />
      </FormGridRow>

      <Grid
        container
        item
        xs={12}
        display="flex"
        justifyContent="space-between"
      >
        <CustomButton
          onClick={onClose}
          disabled={false}
          variant="outlined"
          color="primary"
          sx={{ height: "36px", borderRadius: "20px" }}
        >
          {t("cancel")}
        </CustomButton>
        <SpinnerButton
          label={t("save")}
          onClick={() => handleSubmit(onSubmit)()}
          disabled={!canSave}
          variant="contained"
          isLoading={isLoading}
          color="primary"
          sx={{ height: "36px", borderRadius: "20px" }}
        />
      </Grid>
    </Grid>
  );
};
